// اقدام‌های سراسری نوبت (IA §2.1، §5): «نوبت جدید» (Alt+B) و «مراجعه بدون نوبت» (Alt+W) از هر صفحه، جابه‌جایی از برگه روز و
// جزئیات، و ادامه ثبت سریع مراجعه‌کننده به رزرو (FLOW-01 ← FLOW-02؛ C-AGENT-313). دیالوگ یک بار در پوسته.
import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import Snackbar from '@mui/material/Snackbar';
import { useAccess } from '@phoenix/auth';
import { useTranslation } from '@phoenix/i18n';
import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import { useNavigate } from 'react-router';
import { BookingDialog, type BookingMode, type BookingPrefill, type BookingResult } from './BookingDialog';
import { appointmentPath } from './common';

export interface SchedulingActions {
  canBook: boolean;
  canWalkIn: boolean;
  canReschedule: boolean;
  openBooking: (prefill?: BookingPrefill) => void;
  openWalkIn: (prefill?: BookingPrefill) => void;
  openReschedule: (prefill: BookingPrefill) => void;
}

const SchedulingActionsContext = createContext<SchedulingActions | null>(null);

export function useSchedulingActions(): SchedulingActions | null {
  return useContext(SchedulingActionsContext);
}

export function SchedulingActionsProvider({ children }: { children: ReactNode }) {
  const { t } = useTranslation('sch');
  const access = useAccess();
  const navigate = useNavigate();
  const full = access.accessMode === 'Full';
  const canBook = full && access.can('sch.appointment.create');
  const canWalkIn = canBook && access.can('sch.appointment.checkin');
  const canReschedule = full && access.can('sch.appointment.update');
  const [dialog, setDialog] = useState<{ open: boolean; mode: BookingMode; prefill?: BookingPrefill }>({
    open: false,
    mode: 'book',
  });
  const [done, setDone] = useState<BookingResult | null>(null);

  const openBooking = useCallback(
    (prefill?: BookingPrefill) => {
      if (canBook) {
        setDialog({ open: true, mode: 'book', prefill });
      }
    },
    [canBook],
  );
  const openWalkIn = useCallback(
    (prefill?: BookingPrefill) => {
      if (canWalkIn) {
        setDialog({ open: true, mode: 'walk-in', prefill });
      }
    },
    [canWalkIn],
  );
  const openReschedule = useCallback(
    (prefill: BookingPrefill) => {
      if (canReschedule) {
        setDialog({ open: true, mode: 'reschedule', prefill });
      }
    },
    [canReschedule],
  );

  useEffect(() => {
    const handler = (event: KeyboardEvent) => {
      if (!event.altKey || event.ctrlKey || event.metaKey) {
        return;
      }
      if (event.code === 'KeyB' || event.key.toLowerCase() === 'b') {
        event.preventDefault();
        openBooking();
      } else if (event.code === 'KeyW' || event.key.toLowerCase() === 'w') {
        event.preventDefault();
        openWalkIn();
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [openBooking, openWalkIn]);

  const value = useMemo(
    () => ({ canBook, canWalkIn, canReschedule, openBooking, openWalkIn, openReschedule }),
    [canBook, canWalkIn, canReschedule, openBooking, openWalkIn, openReschedule],
  );

  const message = done
    ? done.mode === 'reschedule'
      ? t('sch:booking.rescheduled')
      : done.mode === 'walk-in'
        ? t('sch:booking.walked_in', { name: done.patientName })
        : t('sch:booking.booked', { name: done.patientName })
    : null;

  return (
    <SchedulingActionsContext.Provider value={value}>
      {children}
      {(canBook || canReschedule) && (
        <BookingDialog
          open={dialog.open}
          mode={dialog.mode}
          prefill={dialog.prefill}
          onClose={() => setDialog((current) => ({ ...current, open: false }))}
          onDone={(result) => {
            setDialog((current) => ({ ...current, open: false }));
            setDone(result);
          }}
        />
      )}
      <Snackbar open={done !== null} autoHideDuration={6000} onClose={() => setDone(null)}>
        <Alert
          severity="success"
          onClose={() => setDone(null)}
          data-testid="booking-success"
          action={
            done ? (
              <Button
                color="inherit"
                size="small"
                onClick={() => {
                  setDone(null);
                  void navigate(appointmentPath(done.appointmentId));
                }}
              >
                {t('sch:booking.open_details')}
              </Button>
            ) : undefined
          }
        >
          {message}
        </Alert>
      </Snackbar>
    </SchedulingActionsContext.Provider>
  );
}
